/**
 * Fragment effects (Explode / Assemble): the element's cached canvas is
 * sliced into an n×n grid and every cell flies on its own seeded trajectory
 * — pure compositing, no geometry work, deterministic per (progress, seed)
 * so export seeking reproduces playback exactly.
 */

export type FragmentConfig = {
  /** 0 intact → 1 fully scattered (tweened) */
  progress: number;
  /** grid resolution per axis, clamped 2..12 */
  count: number;
  /** flight distance in element-size fractions */
  spread: number;
  /** downward pull strength */
  gravity: number;
  /** max tumble, in half-turns */
  twist: number;
  /** shard mode: each grid cell splits into two seeded triangles */
  shards?: boolean;
};

export type FragmentCell = {
  i: number;
  j: number;
  /** displacement of the cell center, dest units */
  dx: number;
  dy: number;
  /** radians */
  rotate: number;
  /** 0..1 */
  alpha: number;
  scale: number;
  /** shard mode: which triangle half of the cell (0/1); unset in grid mode */
  tri?: 0 | 1;
  /** shard mode: diagonal direction — 0 = TL→BR, 1 = TR→BL */
  diag?: 0 | 1;
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/** Deterministic pseudo-random in [0, 1) from two integers — same shape as
 * textEffects' prand. */
const prand = (a: number, b: number) => {
  let h = (a * 374761393 + b * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
};

/** Per-cell states for one fragment frame. p=0 is exact identity — the
 * resting grid reassembles the original image. */
export const getFragmentCells = (
  frag: FragmentConfig,
  seed: number,
  destW: number,
  destH: number,
): { cells: FragmentCell[]; n: number } => {
  const n = Math.max(2, Math.min(12, Math.round(frag.count)));
  const p = clamp01(frag.progress);
  const size = Math.max(destW, destH);
  const cells: FragmentCell[] = [];
  // shard mode emits two triangle halves per cell, each on its own seed
  const halves = frag.shards ? 2 : 1;
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const cellIdx = i * n + j;
      const diag = frag.shards
        ? ((prand(seed + cellIdx, 5) < 0.5 ? 0 : 1) as 0 | 1)
        : undefined;
      for (let half = 0; half < halves; half++) {
        const idx = cellIdx + half * n * n;
        const cellCx = ((i + 0.5) / n) * destW;
        const cellCy = ((j + 0.5) / n) * destH;
        let vx = cellCx - destW / 2;
        let vy = cellCy - destH / 2;
        const len = Math.hypot(vx, vy);
        if (len < 1e-6) {
          // dead-center cell: seeded direction so it doesn't just sit there
          const angle = prand(seed + idx, 4) * Math.PI * 2;
          vx = Math.cos(angle);
          vy = Math.sin(angle);
        } else {
          vx /= len;
          vy /= len;
        }
        // seeded jitter rotates the radial direction a little per cell
        const jitter = (prand(seed + idx, 3) - 0.5) * 0.8;
        const cos = Math.cos(jitter);
        const sin = Math.sin(jitter);
        const ux = vx * cos - vy * sin;
        const uy = vx * sin + vy * cos;
        const dist =
          p * frag.spread * size * (0.4 + prand(seed + idx, 1) * 0.9);
        // + 0 normalizes -0 at p=0 so identity is exact
        cells.push({
          i,
          j,
          dx: ux * dist + 0,
          dy: uy * dist + frag.gravity * p * p * size * 0.5 + 0,
          rotate: (prand(seed + idx, 2) - 0.5) * frag.twist * p * Math.PI + 0,
          // opaque for the first ~37% of the flight, then fades to 0 at p=1
          alpha: clamp01(1 - Math.max(0, 1.6 * p - 0.6)),
          scale: 1 - 0.3 * p,
          ...(frag.shards ? { tri: half as 0 | 1, diag } : {}),
        });
      }
    }
  }
  return { cells, n };
};
